import {
  getAdditionalUserInfo,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  User,
} from "firebase/auth";
import { auth } from "../firebase";

export interface GoogleSignInResult {
  user: User;
  /** Første innlogging med denne Google-kontoen (ny bruker i Firebase). */
  isNewUser: boolean;
}

const provider = () => {
  const p = new GoogleAuthProvider();
  p.setCustomParameters({ prompt: "select_account" });
  return p;
};

/** Koder der brukeren selv lukket vinduet: ingen feilmelding. */
const CANCELLED = [
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
];

/** Popup-vinduet kan ikke åpnes (blokkert, eller nettleser/webview uten popup): bruk redirect. */
const NEEDS_REDIRECT = [
  "auth/popup-blocked",
  "auth/operation-not-supported-in-this-environment",
];

export const isGoogleCancel = (code?: string) =>
  !!code && CANCELLED.includes(code);

/**
 * «Logg inn med Google». Popup fungerer også på mobil (åpnes som ny fane); redirect brukes bare
 * når popup ikke er mulig. Returnerer null når siden sendes videre til Google (redirect), og
 * resultatet hentes da med [completeGoogleRedirect] når brukeren kommer tilbake.
 */
export async function signInWithGoogle(): Promise<GoogleSignInResult | null> {
  try {
    const result = await signInWithPopup(auth, provider());
    return {
      user: result.user,
      isNewUser: getAdditionalUserInfo(result)?.isNewUser === true,
    };
  } catch (err: any) {
    if (NEEDS_REDIRECT.includes(err?.code)) {
      await signInWithRedirect(auth, provider());
      return null;
    }
    throw err;
  }
}

/** Resultatet etter en redirect-innlogging (null når siden ikke ble åpnet fra Google). */
export async function completeGoogleRedirect(): Promise<GoogleSignInResult | null> {
  const result = await getRedirectResult(auth);
  if (!result) return null;
  return {
    user: result.user,
    isNewUser: getAdditionalUserInfo(result)?.isNewUser === true,
  };
}

export function googleErrorMessage(code?: string): string | null {
  if (isGoogleCancel(code)) return null;
  switch (code) {
    case "auth/network-request-failed":
      return "Ingen nettverkstilkobling. Sjekk internett og prøv igjen.";
    case "auth/account-exists-with-different-credential":
      return "Det finnes allerede en bruker med denne e-postadressen. Logg inn med e-post og passord.";
    case "auth/popup-blocked":
      return "Nettleseren blokkerte innloggingsvinduet. Tillat popup-vinduer for siden og prøv igjen.";
    case "auth/unauthorized-domain":
    case "auth/operation-not-allowed":
      return "Google-innlogging er ikke slått på for denne siden ennå. Kontakt iBønn.";
    case "auth/user-disabled":
      return "Denne brukeren er deaktivert. Kontakt iBønn.";
    case "auth/too-many-requests":
      return "For mange forsøk. Vent litt og prøv igjen.";
    default:
      return "Innlogging med Google feilet. Prøv igjen.";
  }
}
